<template>
  <div :class="['table-wrapper', tableClass]">

    <!-- 태그 필터 -->
    <div v-if="showTagFilter" class="tag-filter">
      <button v-for="(tagItem, tag) in tagClasses" :key="tag" class="tag-button"
        :class="[tagItem.class, { active: selectedTags.has(tag) }]" @click="toggleTag(tag)">
        {{ tagItem.label  }}
      </button>
    </div>

    <!-- 체크박스 필터 -->
    <div v-if="showCheckboxFilter" class="checkbox-filter">
      <label
        v-for="(checkboxItem, key) in checkboxClasses"
        :key="key"
      >
        <input
          class="checkbox"
          type="checkbox"
          :value="key"
          :checked="selectedCheckboxes.has(key)"
          @change="toggleCheckbox(key)"
        />
        {{ checkboxItem.label }}
      </label>
    </div>

    <!-- 데이터가 없을 경우 메시지 표시 -->
    <div v-if="isTableEmpty && !loading" class="no-data-message">
      <div class="no-data-img">
        <img :src="getAssetImageUrl('app/status/no-data.svg')" alt="">
      </div>
     <p> {{ emptyMessageComputed }}</p>
    </div>

    <!-- 다이나믹 테이블 -->
    <p v-else>
    <table class="dynamic-table">
      <thead v-if="showColumnTitles">
        <tr>
          <th v-for="(col, index) in columns" :key="index" :class="col.class">
            {{ col.label }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, rowIndex) in props.rows" :key="row.id || rowIndex" :class="{ selected: selectedRowId === (row.id || row) }"
          class="row" @click="handleRowClick(row)">
          <td v-for="(col, colIndex) in columns" :key="colIndex" :class="col.class">

            <!-- 태그 type: 'tag' -->
            <template v-if="col.type === 'tag'">
              <span class="tag" :class="tagClasses[row[col.key]].class" aria-label="Tag Indicator">
              </span>
            </template>

            <!-- 이미지삽입 type: 'image' -->
            <template v-else-if="col.type === 'image'">
              <div class="image-container">
                <img v-if="row[col.key]" :src="row[col.key]" class="image" />
              </div>
            </template>

            <!-- 프로필 아이콘-->
            <template v-else-if="col.type === 'profile-icon' && profileIcon">
              <div v-if="row[col.key]" class="employee-profile" :style="row[col.key].style">
                <h4 class="accent">{{ row[col.key].text }}</h4>
              </div>
            </template>

            <!-- 링크 type: 'action' , 디폴트 actionText '연결'-->
            <template v-else-if="col.type === 'action'">
              <p v-if="rowIndex === props.loadingIndex" class="spinner-wrap">
                <SpinnerIcon />
              </p>
              <p v-else-if="col.action"
                class="link" @click.stop="$emit('actionClick', { action: col.action, row: row, index: rowIndex })">
                {{ col.actionText || '연결' }}
              </p>
            </template>

            <!-- 데이터 표시 -->
            <template v-if="!col.type || col.type === 'text'">
                <span
                  :class="['data', row.class ? row.class : '']"
                  :data-index="`${rowIndex}-${colIndex}`"
                >
                  {{ col.format ? col.format(row[col.key]) : row[col.key]  }}
                </span>
            </template>

            <!--체크박스 표시-->
            <template v-else-if="col.type === 'checkbox'">
              <input v-model="checkedItems" type="checkbox" :value="row" checked />
            </template>

            <!-- 인풋 표시 -->
            <template v-else-if="col.type === 'input'">
              <div class="input-with-unit">
                <input
                  class="input-small"
                  type="number"
                  :value="row[col.key]"
                  min="0"
                  @input="$emit('updateInput', { row, key: col.key, value: +$event.target.value })"
                  />
                  <span v-if="col.unit" class="unit-label dim smaller">{{ col.unit }}</span>
                </div>
            </template>

            <!-- 문자 입력용 input-text -->
            <template v-else-if="col.type === 'input-text'">
              <div class="input-with-unit">
                <input
                  class="input-small"
                  type="text"
                  :value="row[col.key]"
                  @input="$emit('updateInput', { row, key: col.key, value: $event.target.value })"
                />
                <span v-if="col.unit" class="unit-label dim smaller">{{ col.unit }}</span>
              </div>
            </template>

            <!-- 추가 아이콘 -->
            <template v-else-if="col.type === 'add'">
              <div class="wrapper-icon" @click.stop="openAddEmployeeModal(row)">
                <AddStaffIcon class="icon-dim" />
              </div>
            </template>

            <!-- 수정 아이콘 -->
            <template v-else-if="col.type === 'edit'">
              <div class="wrapper-icon" @click.stop="$emit('actionHoverClick', row)">
                <EditIcon class="icon-dim" />
              </div>
            </template>

            <!-- 자세히보기 아이콘 -->
            <template v-else-if="col.type === 'showMore'">
              <div class="wrapper-icon" @click.stop="$emit('showMoreClick', row)">
                <ShowMoreIcon class="icon-dim" />
              </div>
            </template>

            <!-- 삭제 아이콘 -->
            <template v-else-if="col.type === 'delete'">
              <div class="wrapper-icon" @click="$emit('deleteClick', row)" @click.stop="$emit('actionClick', row)">
                <TrashcanIcon class="icon-dim" />
              </div>
            </template>

            <template v-else-if="col.type === 'slot'" >
              <slot name="slot" :row="row" :rowIndex="rowIndex"></slot>
            </template>
          </td>
        </tr>
      </tbody>
    </table>
    </p>
  </div>

  <p v-if="loading" class="empty-message" style="width: 100%">
    <LoadingIcon />
  </p>

  <!-- 더 보기 버튼 -->
  <p v-if="showMoreButton && !loading" class="more-wrap">
    <ButtonProcess secondary :loading="false" :outline="true" @click="clickMore">
      {{ moreButtonTextComputed }}
    </ButtonProcess>
  </p>
</template>

<script lang="ts" setup>
import { computed, inject, nextTick, ref, watch } from 'vue';

import AddStaffIcon from "@/components/icons/IconAddstaff.vue";
import EditIcon from "@/components/icons/IconEdit.vue";
import LoadingIcon from "@/components/icons/IconLoading.vue";
import ShowMoreIcon from "@/components/icons/IconShowMore.vue";
import SpinnerIcon from "@/components/icons/IconSpinner.vue";
import TrashcanIcon from "@/components/icons/IconTrashcan.vue";
import ButtonProcess from "@/components/shared/base/ButtonProcess.vue";
import { useAllTranslations } from "@/static/messageBylanguages.js";
import { getAssetImageUrl } from '@/utils/getAssetImageUrl';

const props = defineProps({
  columns: {
    type: Array,
    required: true,
  },
  rows: {
    type: Array,
    required: true,
  },
  rowsPerPage: {
    type: Number,
    default: 10, // 한페이지당 행 갯수
  },
  showColumnTitles: {
    type: Boolean,
    default: true, // 타이틀 숨기기, 보여지는것이 default
  },
  tagClasses: { // 태그 스타일
    type: Object,
    required: false,
  },
  checkboxClasses: { // 체크박스 스타일
  type: Object,
  required: false,
  default: () => ({}),
},
  profileIcon: {
    type: Boolean,
    default: false,
  },
  showCheckbox: {
    type: Boolean,
    default: false,
  },
  triggerCheckBox: {
    type: Boolean,
    default: false,
  },
  showMoreButton: {
    type: Boolean,
    default: true, // "더 보기" 버튼 보이기
  },
  moreButtonText: { // "더 보기" 버튼 텍스트
    type: String,
  },
  showTagFilter: { // 필터 보이기
    type: Boolean,
    default: false,
  },
  showCheckboxFilter: { // 체크박스 필터 보이기
  type: Boolean,
  default: false,
  },
  checkboxOptions: { // 체크박스 필터 옵션
    type: Array,
    default: () => [],
  },
  enableRowClick: { // 클릭하여 선택하기
    type: Boolean,
    default: false,
  },
  unit: {  // INPUT단위 예: '원', '원/분'
    type: String,
    default: '',
  },
  emptyMessage: {
    type: String,
  },
  tableClass: {
    type: String,
    default: '',
  },
  loading: {
    type: Boolean,
    default: false,
  },
  changeSelected: {
    type: Object,
    default: () => ({}),
  },
  loadingIndex: {
    type: Number,
    default: -1,
  },
});

const translations = useAllTranslations();
const moreButtonTextComputed = computed(() => props.moreButtonText || translations.button_load_more.value)
const emptyMessageComputed = computed(() => props.emptyMessage || translations.alert_no_saved_info.value)

const openAddEmployeeModal = inject("openAddEmployeeModal", () => {})

const emit = defineEmits(['rowClick', 'actionClick', 'tagFilterChange', 'actionHoverClick', 'deleteClick', 'moreClick', 'fetchCheckBox', 'updateInput', 'showMoreClick']);

const selectedTags = ref(new Set());
const selectedRowId = ref(null);
const isTableEmpty = computed(() => props.rows.length === 0);
const checkedItems = ref([]);

const clickMore = () => {
  emit('moreClick');
};

//태그 토글하기
const toggleTag = (tag) => {
  if (selectedTags.value.has(tag)) {
    selectedTags.value.delete(tag);
  } else {
    selectedTags.value.add(tag);
  }
  emit('tagFilterChange', Array.from(selectedTags.value));
};

// 체크박스 필터 선택 상태
const selectedCheckboxes = ref(new Set());

// 체크박스 토글
const toggleCheckbox = (key: string) => {
  if (selectedCheckboxes.value.has(key)) {
    selectedCheckboxes.value.delete(key);
  } else {
    selectedCheckboxes.value.add(key);
  }
  emit('checkboxFilterChange', Array.from(selectedCheckboxes.value));
};

// 행 클릭 이벤트 처리
const handleRowClick = (row) => {
  if (props.enableRowClick) {
    if (selectedRowId.value === (row.id || row)) {
      selectedRowId.value = null;
      emit('rowClick', null);
    } else {
      selectedRowId.value = row.id || row;
      emit('rowClick', row);
    }
  }
};

// 툴팁 ellipsis있을때만 보이기
const showTooltipMap = ref({});
const isEllipsisActive = (el) => {
  return el && el.scrollWidth > el.clientWidth;
};

watch(
  () => props.rows,
  () => {
    nextTick(() => {
      const newMap = {};
      const elements = document.querySelectorAll('.data[data-index]');
      elements.forEach((el) => {
        const index = el.dataset.index;
        newMap[index] = isEllipsisActive(el);
      });
      showTooltipMap.value = newMap;
    });
  },
  { immediate: true }
);

watch(
  () => props.changeSelected,
  (newVal) => {
    if (newVal && selectedRowId.value !== newVal) {
      selectedRowId.value = newVal.id;
    }
  }
);

watch(
  () => props.triggerCheckBox,
  (newVal) => {
    if (newVal) {
      emit('fetchCheckBox', checkedItems.value);
    }
  }
);

watch(
  () => props.rows,
  (newVal) => {
    checkedItems.value = newVal;
  },
  { immediate: true }
);
</script>

<style scoped lang="scss">

h4 {
  color: inherit;
}

.table-wrapper {
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  position: relative;
  // overscroll-behavior: contain;
}

.no-data-message {
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
  text-align: center;
  padding: 1rem 0 3rem;
  gap: 0.5rem;
  p{
    font-size: $font-size-psmall;
    color: $color-gray-100;
  }
}

.dynamic-table {
  width: 100%;
  border-collapse: separate;
  table-layout: fixed;
  min-width: 35rem;
  border-spacing: 0;
  overflow: visible;
  position: relative;
  z-index: 0;
  tr{
    th{
       height: 2rem;
        line-height: 2rem;
        font-size: $font-size-psmall;
        color: $color-gray-200;
        text-align: left;
        position: sticky;
        padding: 0.25rem;
        top: 0;
        background: #fff;
        z-index: 2;
        &:first-child {
          padding-left: 1rem;
        }
    }
  }
  tbody {
      tr {
        &:nth-child(odd) {
          background: #fafafb;
        }
        td {
          &:first-child {
            border-top-left-radius: 8px;
            border-bottom-left-radius: 8px;
          }
          padding: 0.25rem;
          font-size: $font-size-psmall;
          vertical-align: middle;
          &:first-child {
            padding-left: 1rem;
          }
          .td-inr {
            min-height: 2.5rem;
            display: flex;
            align-items: center;
            .icon {
              margin-left: $size-xs;
            }
          }
          .type {
            display: flex;
            height: 100%;
            align-items: center;
            gap: $size-s;
            .icon-box {
              width: 1.75rem;
              height: 1.75rem;
              border-radius: 12px;
              background: #fff;
              padding: 4px;
              img {
                width: 100%;
                height: 100%;
                object-fit: contain;
              }
              &.audience {
                border: 1px solid $audience-line;
              }
              &.conference {
                border: 1px solid $conference-line;
              }
              &.single {
                border: 1px solid $single-line;
              }
              &.general {
                border: 1px solid $general-line;
              }
              &.meeting {
                border: 1px solid $meeting-line;
              }
            }
          }
          .ag {
            gap: $size-s;
            justify-content: flex-start;
            span {
              line-height: 130%;
            }
          }
          .situation {
            gap: $size-s;
            .clear {
              color: var(--primary);
              svg {
                margin-left: $size-xs;
              }
            }
            div {
              display: flex;
              align-items: center;
            }
          }
        }
      }
    }
}

.row.selected {
  border-radius: 8px;
  background-color: var(--surface-hover);
  overflow: hidden;
}

.data {
  display: block;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: normal;
  white-space: nowrap;
  padding: 0;
  margin: 0;

  &.prewrap-data {
    white-space: pre-wrap;
  }
}

.wrapper-icon {
  display: flex;
  width: 2rem;
  height: 2rem;
  border-radius: 4px;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.wrapper-icon:hover {
  background-color: var(--surface-gray);
}

.input-with-unit {
  position: relative;
  display: inline-block;
  width: 100%;
}

.unit-label {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
}

.image-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.image {
  width: 35px;
  height: 35px;
  border-radius: 8px;
}

.employee-profile {
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
  border-radius: 50%;
  flex-shrink: 0;
  background-color: var(--surface-gray-dim);
}

// 체크박스필터
.checkbox-filter {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

// 태그필터
.tag-filter {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.tag-button {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.5rem 1rem;
  border-radius: 16px;
  background: var(--surface-gray-dimmest);
  color: var(--font-dim);
  word-break: keep-all;
  transition: background-color 0.3s ease, color 0.3s ease;
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  flex-shrink: 0
}

.tag-button.active {
  color: var(--font-white);
}

.tag {
  display: inline-block;
  width: 0.25rem;
  height: 3rem;
  vertical-align: middle;
  transition: all 0.4s ease;
}

.tag1 {
  background-color: var(--warning);
}

.tag2 {
  background-color: var(--color-navy-80);
}

.tag3 {
  background-color: var(--color-green);
}

.tag4 {
  background-color: var(--color-pink-light);
}

.tag5 {
  background-color: var(--color-purple-light);
}

.tag-button.tag1 {
  background-color: var(--warning-dimmer);
  color: var(--warning);
}

.tag-button.tag1.active {
  background-color: var(--warning-mild);
  color: var(--font-white);
  box-shadow: unset;
}

.tag-button.tag2 {
  background-color: var(--info-dimmer);
  color: var(--primary);

}

.tag-button.tag2.active {
  background-color: var(--info);
  color: var(--font-white);
  box-shadow: unset;
}

.tag-button.tag3 {
  background-color: var(--success-dimmer);
  color: var(--success-dark);

}

.tag-button.tag3.active {
  background-color: var(--color-green-dark);
  color: var(--font-white);
  box-shadow: unset;
}

.tag-button.tag4 {
  background-color: var(--color-pink-light);
  color: var(--color-pink-dark);

}

.tag-button.tag4.active {
  background-color: var(--color-pink-dark);
  color: var(--font-white);
  box-shadow: unset;
}

.tag-button.tag5 {
  background-color: var(--color-purple-light);
  color: var(--color-purple-dark);

}

.tag-button.tag5.active {
  background-color: var(--color-purple-dark);
  color: var(--font-white);
  box-shadow: unset;
}

.more-wrap {
  display: flex;
  justify-content: center;
  width: 100%;
  button{
    width: auto;
    padding: 0 1.5rem;
    border-radius: 100px;
  }
}

/* 테이블 스타일, working file script 에서 적용 */
.width4px {
  width: 0.25rem;
}

.width5 {
  width: 5%;
}

.width10 {
  width: 10%;
}

.width15 {
  width: 13.33%;
}

.width15 {
  width: 15%;
}

.width20 {
  width: 20%;
}

.width25 {
  width: 25%;
}

.width30 {
  width: 30%;
}

.width35 {
  width: 35%;
}

.width40 {
  width: 40%;
}

.width50 {
  width: 50%;
}

.width60 {
  width: 60%;
}

.width70 {
  width: 70%;
}

.width100 {
  width: 100%;
}
.width1rem {
  width: 1rem;
  min-width: 1rem;
}

.width2rem {
  width: 2rem;
  min-width: 2rem;
}

.width3rem {
  width: 3rem;
  min-width: 3rem;
  flex-shrink: 0;
}

.width4rem {
  width: 4rem;
  min-width: 4rem;
}

.width5rem {
  width: 5rem;
  min-width: 5rem;
}

.width6rem {
  width: 6rem;
  min-width: 6rem;
}

.width7rem {
  width: 7rem;
  min-width: 7rem;
}

.width8rem {
  width: 8rem;
  min-width: 8rem;
}

.width10rem {
  width: 10rem;
  min-width: 10rem;
}

.width15rem {
  width: 15rem;
  min-width: 15rem;
}

.widthfit {
  width: fit-content;
}

.middle {
  text-align: center;
  vertical-align: middle;
}

.left {
  text-align: left;
}

.padding0 {
  padding: 0;
}

.paddingleft {
  padding-left: 1rem;
}

.end {
  position: absolute;
  right: 0;
}

.spinner-wrap {
  display: flex;
  justify-content: center;
}

@include respond-to(mobile-landscape) {
  .paddingleft {
    padding-left: 1.5rem;
  }
  .table-wrapper{
    &::-webkit-scrollbar{
      display: none;
    }
  }
}
</style>
